import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { FieldOption } from "@stripe/stripe-js";
import Button from "components/form/Button";
import { useUserData } from "store/slices/authSlice";
import {
  checkOverPaymentAmount,
  checkPhone,
  formatCurrency,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import SecureLock from "icons/payments/SecureLock";
import {
  duplicatePaymentGet,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymentElement,
  storePaymnetPlan,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { SettingsValue } from "types/payments";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  updateCheckAmountState,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { PhoneInput } from "types/common";
import { getCountryCallingCode } from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import { RootState } from "store/store";
import MobileInputNew from "components/form/MobileInputNew";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { useTid } from "utils/testid";
import { getPaymentTotalWithFee } from "utils/paymentFee";
import { buildAccountBillingDetails } from "utils/stripeBillingDetails";

interface StripeBankFormProps {
  amount?: any;
  isEdit?: boolean;
  isPaymentLink?: boolean;
  email: string;
  setEmail: (value: string) => void;
  isSecretLoading?: boolean;
  isRecurring?: boolean;
  paymentPlan?: any;
  stripeBankURL?: string;
  isRetry?: boolean;
  setIsDisable?: any;
  isAgreement?: boolean;
  handleBack?: any;
  isUpdatePlan?: boolean;
  setShowPreview?: any;
  isSchedulePayment?: boolean;
  isProductLink?: boolean;
  isUpdateSubscription?: boolean;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  isUpdateSubscriptionPaymentMethod?: boolean;
  showConvFee?: boolean;
  isDowngradePlan?: boolean;
  handleEmailBlur?: () => void;
  orderId?: number;
  onIntentFailure?: (orderId: number, error: any) => Promise<void>;
}
let globalStripeBankInstance: any = null;
let globalStripeBankElements: any = null;

const StripeBankForm = forwardRef((props: StripeBankFormProps, ref) => {
  const tid = useTid();
  const {
    amount = 0,
    isEdit = false,
    isPaymentLink = true,
    email,
    setEmail,
    handleEmailBlur = () => {},
    isSecretLoading = false,
    isRecurring = false,
    isUpdateSubscriptionPaymentMethod = false,
    paymentPlan,
    stripeBankURL = "",
    isRetry = false,
    setIsDisable = () => {},
    isAgreement = false,
    handleBack = () => {},
    isUpdatePlan = false,
    setShowPreview = () => {},
    isSchedulePayment = false,
    isProductLink = false,
    isUpdateSubscription = false,
    checkNegativeBalance = false,
    accounts_id = "",
    collectPhone = false,
    phoneNumber = { phone: "", country_code: "", postal_code: "" },
    setPhoneNumber = () => {},
    showConvFee = false,
    isDowngradePlan = false,
    orderId = 0,
    onIntentFailure = async () => {},
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const userData = useUserData();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const portalSettings = usePortalSettingsData();
  const {
    companyDetails,
    conFee,
    isFeeLoading,
    paymentElement,
    checkedDuplicatePayment,
  } = useAppSelector((state: RootState) => state.payment);
  const { isChecking, isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );

  const { checkAmount } = useAppSelector((state: RootState) => state.common);

  const dispatch = useAppDispatch();
  const [isPaymentDisable, setIsPaymentDisable] = useState<boolean>(true);
  const showFinalDisclosure = useShowFinalDisclosure();

  const handlePhoneCountryChange = useCallback((itm: string) => {
    setPhoneNumber((state) => ({
      ...state,
      country_code: getCountryCallingCode(itm as CountryCode),
    }));
  }, []);

  const handleOnPhoneChange = useCallback((phone: string) => {
    setPhoneNumber((state) => ({
      ...state,
      phone,
    }));
  }, []);

  const handlePayment = async () => {
    if (!stripe || !elements) {
      return;
    }

    if (paymentPlan && !isRetry) {
      await dispatch(storePaymnetPlan(paymentPlan));
    }

    const billingDetails = buildAccountBillingDetails({
      account: userData?.data?.accountData,
      // The form's email box: the account's email, or what a customer without
      // one typed (Pay waits for it). The Payment Element hides its own email
      // field, so Stripe refuses the payment without this.
      email: email || userData?.data?.accountData?.email,
      phone: collectPhone ? phoneNumber?.phone : undefined,
      nameFallback: email,
    });

    const activeStripe =
      showFinalDisclosure && globalStripeBankInstance
        ? globalStripeBankInstance
        : stripe;
    const activeElements =
      showFinalDisclosure && globalStripeBankElements
        ? globalStripeBankElements
        : elements;

    const { error } = await activeStripe.confirmPayment({
      elements: activeElements,
      confirmParams: {
        return_url: stripeBankURL,
        payment_method_data: {
          billing_details: billingDetails,
        },
      },
    });

    // Reset payment processing after redirect is initiated
    setIsLoading(false);
    dispatch(setIsPaymentProcessing(false));

    if (error) {
      await onIntentFailure(orderId, error);
      console.log(error);
      setIsLoading(false);
      await dispatch(onSetShowDisclosure(false));
      await dispatch(onSetFinalDisclosureLoading(false));
    }
  };

  const handleSetup = async () => {
    if (!stripe || !elements) {
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    if (paymentPlan && !isRetry) {
      await dispatch(storePaymnetPlan(paymentPlan));
    }

    const billingDetails = buildAccountBillingDetails({
      account: userData?.data?.accountData,
      // The form's email box: the account's email, or what a customer without
      // one typed (Pay waits for it). The Payment Element hides its own email
      // field, so Stripe refuses the payment without this.
      email: email || userData?.data?.accountData?.email,
      phone: collectPhone ? phoneNumber?.phone : undefined,
      nameFallback: email,
    });

    const activeStripe =
      showFinalDisclosure && globalStripeBankInstance
        ? globalStripeBankInstance
        : stripe;
    const activeElements =
      showFinalDisclosure && globalStripeBankElements
        ? globalStripeBankElements
        : elements;

    const { error } = await activeStripe.confirmSetup({
      elements: activeElements,
      confirmParams: {
        return_url: stripeBankURL,
        payment_method_data: {
          billing_details: billingDetails,
        },
      },
    });

    // Reset payment processing after redirect is initiated
    setIsLoading(false);
    dispatch(setIsPaymentProcessing(false));

    if (error) {
      await onIntentFailure(orderId, error);
      console.log(error.message);
      setIsLoading(false);
      await dispatch(onSetShowDisclosure(false));
      await dispatch(onSetFinalDisclosureLoading(false));
    }
  };

  const checkPastAmount = async () => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id ?? accounts_id,
        payment_method: "ACH",
        amount: amount,
      };
      let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async () => {
    try {
      let moveAhead =
        !checkedDuplicatePayment && !isProductLink
          ? await checkPastAmount()
          : true;
      if (!moveAhead) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        dispatch(storePaymentElement(elements));
        dispatch(onSetFinalDisclosureLoading(false));
        if (paymentPlan && !isRetry) {
          await dispatch(storePaymnetPlan(paymentPlan));
        }
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          let element = elements ? elements : paymentElement;
          dispatch(storePaymentElement(element));
          globalStripeBankInstance = stripe;
          globalStripeBankElements = element;
          setIsLoading(false);
          dispatch(setIsPaymentProcessing(false));
          dispatch(resetDuplicateOrder());
          dispatch(onSetFinalDisclosureLoading(false));
          if (paymentPlan && !isRetry) {
            await dispatch(storePaymnetPlan(paymentPlan));
          }
          dispatch(onSetShowDisclosure(true));
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    dispatch(setIsPaymentProcessing(true));
    if (paymentPlan && !isRetry) {
      await dispatch(storePaymnetPlan(paymentPlan));
    }
    if (!isUpdateSubscriptionPaymentMethod) {
      let negativeBalance =
        !checkAmount && checkNegativeBalance
          ? await checkOverPaymentAmount({
              amount: Number(amount),
              balance: userData?.data?.accountData?.balance,
              overpayment_protection: portalSettings?.overpayment_protection,
              dispatch,
            })
          : true;
      if (!negativeBalance) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      let moveAhead = await handleCheckDisclosureAndDuplicate();
      if (!moveAhead) {
        return;
      }
      await dispatch(updateCheckAmountState(false));
    }

    dispatch(setIsPaymentProcessing(false));
    if (isRecurring) {
      await handleSetup();
    } else {
      await handlePayment();
    }
  };

  const paymentElementOptions: {
    fields: {
      billingDetails: {
        name?: FieldOption;
        email?: FieldOption;
      };
    };
  } = {
    fields: {
      billingDetails: {
        name: "never",
        email: "never",
      },
    },
  };

  // Pay is enabled once the bank details are complete. The element reports
  // its own changes through onChange. Looking it up after mount instead
  // assumed it already existed, and crashed the page when it did not.
  const handlePaymentElementChange = (e: any) => {
    if (!e.empty && e.complete) {
      setIsDisable(false);
      setIsPaymentDisable(false);
    } else {
      setIsDisable(true);
      setIsPaymentDisable(true);
    }
  };

  useEffect(() => {
    if (userData?.data?.accountData?.email) {
      setEmail(userData?.data?.accountData?.email || "");
    }
  }, [userData]);

  useImperativeHandle(ref, () => ({
    callHandleClick: () => handleSubmit(),
  }));

  const amountCheck = useMemo(() => {
    if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
      return false;
    }

    return amount < 1 || isEdit;
  }, [amount, isDowngradePlan, isUpdateSubscriptionPaymentMethod, isEdit]);

  return (
    <div data-testid={tid("stripe-bank-form")} aria-label="Stripe bank form">
      <div
        className="relative"
        data-testid={tid("stripe-bank-element-section")}
      >
        <div
          className={`w-full absolute ${
            isPaymentDisable ? "-top-2" : "top-2"
          } right-0  flex justify-between items-center text-txtSecondary`}
        >
          {isPaymentDisable ? (
            <div
              className="text-xs font-bold leading-[15.62px] text-txtSecondary  uppercase"
              data-testid={tid("stripe-bank-label")}
            >
              Bank Account
            </div>
          ) : (
            <div></div>
          )}
          <span className="text-xs text-txtSecondary font-medium flex gap-1 items-center justify-center">
            <SecureLock aria-hidden="true" />
            Secured by {companyDetails?.name || companyDetails?.company_name}
          </span>
        </div>
        <div
          data-testid={tid("stripe-bank-payment-element")}
          aria-label="Bank account"
        >
          <PaymentElement
            options={paymentElementOptions}
            onChange={handlePaymentElementChange}
          />
        </div>
      </div>

      {showConvFee &&
        portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
          <ConvenienceFeeDisplay
            conFee={conFee}
            isFeeLoading={isFeeLoading}
            isPaymentLink={isPaymentLink}
            setShowPreview={setShowPreview}
          />
        )}
      <div
        className={`w-full py-5 ${isUpdatePlan || isAgreement ? "mb-5" : ""}`}
      >
        <div className="text-xs font-bold leading-[18px] text-txtSecondary mb-2 mt-3">
          EMAIL
        </div>
        <div className="relative w-full ">
          <input
            autoComplete="off"
            data-testid={tid("stripe-bank-email-input")}
            aria-label="Email address"
            className={`w-full border-strokePrimary rounded-[5px] hover:!border-strokeActive border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
              userData?.data?.accountData?.email
                ? "!cursor-not-allowed	!bg-bgSecondary"
                : "cursor-text"
            }`}
            placeholder="Email address"
            onBlur={handleEmailBlur}
            onChange={(e) => {
              setEmail(
                userData?.data?.accountData?.email
                  ? userData?.data?.accountData?.email
                  : e.target.value
              );
            }}
            value={
              userData?.data?.accountData?.email
                ? userData?.data?.accountData?.email
                : email
            }
            disabled={userData?.data?.accountData?.email}
          />
        </div>

        {collectPhone && (
          <div className="pt-5">
            <div
              className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
            >
              Phone
            </div>
            <MobileInputNew
              inputValue={phoneNumber?.phone || ""}
              isProductLinkInput={true}
              userCountry={
                getCountryShortName(phoneNumber?.country_code) || "US"
              }
              setCountry={handlePhoneCountryChange}
              setInputValue={handleOnPhoneChange}
              data-testid={tid("stripe-bank-phone-input")}
              aria-label="Phone number"
            />
          </div>
        )}
      </div>
      {!isRetry &&
        (isAgreement ? (
          <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
            <Button
              text={`Back`}
              variant="white"
              onClick={handleBack}
              className="lg:w-40 w-full max-w-40"
              data-testid={tid("stripe-bank-back-button")}
              aria-label="Go back from Stripe bank payment"
            />
            <Button
              text={`Confirm`}
              isDisabled={
                amountCheck || email.trim() === "" || isPaymentDisable
              }
              isLoading={isLoading || isSecretLoading}
              onClick={() => {
                handleSubmit();
              }}
              className="lg:w-40 w-full max-w-40"
              variant="variable"
              data-testid={tid("stripe-bank-confirm-button")}
              aria-label="Confirm Stripe bank payment"
            />
          </div>
        ) : isSchedulePayment ? (
          <div
            className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5"
          >
            <div
              className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
            >
              {isUpdatePlan && (
                <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
              )}

              <div className="w-full flex justify-between gap-5">
                <Button
                  className="lg:!w-40 !w-full"
                  isDisabled={isLoading}
                  text="Back"
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                  data-testid={tid("stripe-bank-schedule-back-button")}
                  aria-label="Go back from scheduled Stripe bank payment"
                />
                <Button
                  className="lg:!w-40 !w-full"
                  isLoading={isLoading || isFeeLoading || isChecking}
                  isDisabled={
                    amountCheck ||
                    email.trim() === "" ||
                    isPaymentDisable ||
                    isFeeLoading ||
                    (isProductLink && !isSubscriptionAllowed)
                  }
                  text={isUpdatePlan ? "Update" : "Confirm"}
                  type="button"
                  onClick={() => {
                    handleSubmit();
                  }}
                  variant="variable"
                  data-testid={tid("stripe-bank-schedule-confirm-button")}
                  aria-label={
                    isUpdatePlan
                      ? "Update scheduled Stripe bank payment"
                      : "Confirm scheduled Stripe bank payment"
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            text={
              isUpdatePlan || isUpdateSubscription
                ? "Update"
                : `Pay ${formatCurrency(getPaymentTotalWithFee(amount, conFee)) || 0.0}`
            }
            className={"!w-full mt-5"}
            isDisabled={
              amountCheck ||
              isEdit ||
              !validateEmail(email) ||
              (collectPhone && !checkPhone(phoneNumber)) ||
              isPaymentDisable ||
              isFeeLoading ||
              (isProductLink && !isSubscriptionAllowed)
            }
            onClick={() => {
              handleSubmit();
            }}
            isLoading={
              isLoading || isSecretLoading || isFeeLoading || isChecking
            }
            variant={"variable"}
            data-testid={tid("stripe-bank-submit-button")}
            aria-label={
              isUpdatePlan || isUpdateSubscription
                ? "Update Stripe bank payment"
                : "Pay with Stripe bank"
            }
          />
        ))}
    </div>
  );
});

export default StripeBankForm;
